import {RegisterInput} from "./RegisterRelatedComponents";
import Editor from "@monaco-editor/react";
import {UrmProgramCompilationInfoComponent} from "./UrmProgramCompilationInfoComponent";
import {UrmProgramParser} from "../../js/entities/UnlimitedRegisterMachine/UrmProgramParser";
import {UrmProgramCompilingError, UrmProgramCompilingException} from "../../js/errors/Exceptions";
import {exportTextFile} from "../../js/utils";
import ExpandableTab from "../components/ExpandableTab";
import { InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';

/**
 *
 * @param createModeState useState-variable of mode state.
 * It contains the following fields:
 * <code>registersInput</code> ({@link number}[]),
 * <code>urmProgramCodeInEditor</code> ({@link string}),
 * <code>compiledUrmProgram</code> ({@link UrmProgram}),
 * <code>urmCompilationErrors</code>: ({@link UrmProgramCompilingError}[])
 * @param setCreateModeState set function of <code>createModeState</code>
 * @param switchToRunMode is callback that changes mode to 'RUN'. Takes no arguments.
 * Should only be called when compiledUrmProgram is not <code>indefined</code>.
 * @constructor
 */
export default function UrmCreateModeComponent({createModeState,
                                                setCreateModeState,
                                                switchToRunMode}) {
    const onUrmProgramCodeInEditorChanged = (newCodeValue, event) => {
        setCreateModeState((state) => ({
            ...state,
            urmProgramCodeInEditor: newCodeValue
        }));
    }

    const onAddRegisterInput = (event) => {
        event.preventDefault();
        setCreateModeState((state) => ({
            ...state,
            registersInput: [...state.registersInput, 0]
        }));
    };

    const onRemoveRegisterInput = (event) => {
        event.preventDefault();
        setCreateModeState((state) => ({
            ...state,
            registersInput: [...state.registersInput.slice(0, state.registersInput.length - 1)]
        }));
    }

    // if register value is edited, need immediately reflect it in array of registers inputs values
    const onEditRegisterInputValue = (registerIndex, newValue) => {
        setCreateModeState((state) => ({
            ...state,
            registersInput: state.registersInput.map((el, index) => {
                if(index === registerIndex){
                    return parseInt(newValue);
                }
                return el;
            })
        }));
    }

    const onCompileUrmProgram = (event) => {
        event.preventDefault();

        let urmProgram;
        let errors;

        const urmProgramCode = createModeState.urmProgramCodeInEditor;
        try{
            urmProgram = UrmProgramParser.parse(urmProgramCode);
        }
        catch(e){
            if (e instanceof UrmProgramCompilingException) {
                errors = e.errors;
            }
            else {
                errors = [new UrmProgramCompilingError(-1, "unable to compile: unknown error")];
            }
        }

        if(urmProgram !== undefined){
            setCreateModeState((state) => ({
                ...state,
                compiledUrmProgram: urmProgram,
                urmCompilationErrors: undefined
            }));
            console.log("compiled URM program:\n" + urmProgram.toString());
        }
        else{
            setCreateModeState((state) => ({
                ...state,
                urmCompilationErrors: errors
            }));
        }
    }

    const onExportUrmProgram = (event) => {
        event.preventDefault();

        if(createModeState.urmProgramCodeInEditor.trim() === ""){ // string is empty, impossible case (btn isn't activated)
            alert("urmProgramCodeInEditor shouldn't be empty");
            return;
        }

        exportTextFile("urm_program.txt", createModeState.urmProgramCodeInEditor);
    }

    // sources: https://developer.mozilla.org/en-US/docs/Web/API/FileReader,
    // https://medium.com/@miniceo/front-end-shorts-how-to-read-content-from-the-file-input-in-react-17f49b293909
    const onImportUrmProgram = (event) => {
        const file = event.target.files[0];

        // validate file existence and type
        if (!file) {
            alert("No file selected. Please choose a file");
            return;
        }
        if (!file.type.startsWith("text")) {
            alert("Unsupported file type. Please select a text file.");
            return;
        }

        // read the file
        const reader = new FileReader();
        reader.onload = () => {
            const textContent = reader.result;

            if(textContent.trim() === ""){
                alert("File can't be empty")
                return;
            }

            setCreateModeState((state) => ({
                ...state,
                urmProgramCodeInEditor: textContent
            }))
        };
        reader.onerror = () => {
            alert("Error reading the file. Please try again.");
        };
        reader.readAsText(file);
    }

    const onSwitchToRunMode = (event) => {
        event.preventDefault();

        if(createModeState.compiledUrmProgram === undefined) { // impossible case (button is inactive)
            alert('Before going into RUN mode, compile the URM program');
            return;
        }

        switchToRunMode(); // callback that actually switches to RUN mode
    }


    return (
        <>
            
            {/* Input registers; addRegisterInput, removeRegisterInput buttons */}
            <h3>Registers
                <button type="button" className="btn btn-primary btn-sm d-inline ms-1" onClick={onAddRegisterInput}>
                    +
                </button>

                <button type="button" className="btn btn-primary btn-sm ms-1" onClick={onRemoveRegisterInput}
                        disabled={createModeState.registersInput.length <= 1}>
                    -
                </button>
            </h3>

            {/* input registers */}
            <div className={'registers-container'}>
                {
                    createModeState.registersInput.map((registerInputContent, index) => (
                        <RegisterInput initContent={registerInputContent} index={index} key={index}
                                       onValueChangedHandler={onEditRegisterInputValue}/>
                    ))
                }
            </div>

            {/* Help about URM commands */}
            <div className="row p-0 my-3">
                <ExpandableTab title={'URM commands help'}>
                    <div className="d-flex flex-column align-items-start text-start">
                        <ul>
                            <li><InlineMath math="Z(i)"/> – set the value of <InlineMath math="i"/>-th
                                register to <InlineMath math="0"/>; goto next command</li>
                            <li><InlineMath math="S(i)"/> – increment the value of <InlineMath math="i"/>-th register;
                                goto next command</li>
                            <li><InlineMath math="T(i,j)"/> – copy the value of <InlineMath math="i"/>-th register
                                to <InlineMath math="j"/>-th register; goto next command</li>
                            <li><InlineMath math="J(i,j,q)"/> – if the value of <InlineMath math="i"/>-th
                                register is equal to the value of <InlineMath math="j"/>-th register, then
                                goto (but not execute) <InlineMath math="q"/>-th command, else goto next command
                            </li>
                        </ul>
                    </div>
                </ExpandableTab>
            </div>

            <div className="row">
                <div className="col-6"><h3>URM program editor</h3></div>
                <div className="col-6"><h3>The latest compiled URM program</h3></div>
            </div>
            <div className="row mb-5">

                <div className="col-6">

                    <div className="urm-program-code-wrapper">
                        {/* editor of URM program */}
                        {/* docs: https://www.npmjs.com/package/@monaco-editor/react#editor */}
                        <Editor
                            height='100%'
                            width='100%'
                            defaultLanguage="javascript"
                            value={createModeState.urmProgramCodeInEditor}
                            onChange={onUrmProgramCodeInEditorChanged}

                            options={{
                                minimap: {enabled: false}, // Disable minimap (optional)
                                lineNumbers: 'on',
                                automaticLayout: true,
                                quickSuggestions: false,
                                snippetSuggestions: 'none',
                                wordBasedSuggestions: 'off',
                                parameterHints: {enabled: false},
                                autoClosingBrackets: 'always',
                                autoClosingQuotes: 'always',
                                hover: {enabled: false},  // Disable hover info
                                codeLens: false,
                                lightbulb: {enabled: 'off'},
                                occurrencesHighlight: 'off',
                                find: { // Disable find/replace highlights
                                    autoFindInSelection: 'never',
                                    seedSearchStringFromSelection: 'always',
                                },
                                renderValidationDecorations: 'off', // Disable error/warning highlights
                                colorDecorators: false,
                                semanticHighlighting: {enabled: false},
                                theme: 'vs', // default theme
                                matchBrackets: 'never', // disable highlighting of matching brackets
                                bracketPairColorization: {enabled: false},
                            }}/>
                    </div>

                    {/* import program, export program, compile program */}
                    <div className="col mt-2">

                        {/* Import program */}
                        <div className="row flex-row align-content-center">
                            <div className="col-4 align-content-center">
                                <label htmlFor="input-import-urm-program"
                                       className="form-label m-0"> Import program: </label>
                            </div>
                            <div className="col">
                                <input type="file" className="form-control" id="input-import-urm-program"
                                       onChange={onImportUrmProgram} accept={'text/plain'}/>
                            </div>
                        </div>

                        <div>
                            <button type="button" className="btn btn-primary m-1 mt-2"
                                    onClick={onExportUrmProgram}
                                    disabled={createModeState.urmProgramCodeInEditor.trim() === ""}>
                                Export
                            </button>
                        </div>

                        <div>
                            <button type="button" className="btn btn-primary m-1" onClick={onCompileUrmProgram}>
                                Compile
                            </button>
                        </div>

                    </div>


                    {/* compilation errors (or message about successful compilation) */}
                    <p className={'m-1 text-start'}>
                        <b>Compilation info:</b>
                    </p>
                    <UrmProgramCompilationInfoComponent
                        compilationErrors={createModeState.urmCompilationErrors}
                        successfullyCompiledUrmProgramExists={createModeState.compiledUrmProgram !== undefined}/>
                </div>


                <div className="col-6">
                    {/* show last compiled URM program */}
                    <div className="urm-program-code-wrapper">
                            <textarea readOnly={true} className={'urm-program-code-container'}
                                      id={'compiled-urm-program'}
                                      value={
                                          createModeState.compiledUrmProgram !== undefined
                                              ? createModeState.compiledUrmProgram.toString()
                                              : ""
                                      }/>
                    </div>

                    {/* show button to switch to RUN mode */}
                    <button type="button" className="btn btn-primary btn-lg mt-2"
                            disabled={createModeState.compiledUrmProgram === undefined}
                            onClick={onSwitchToRunMode}>
                        RUN mode
                    </button>
                    {(createModeState.compiledUrmProgram === undefined) &&
                        (
                            <p className={'text-muted small'}>
                                Before going into RUN mode, compile the URM program
                            </p>
                        )
                    }

                </div>

            </div>
        </>
    );
}
